import { useTranslation } from "react-i18next";
import { CheckIcon, CopyIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { useCopyToClipboard, useTimeout } from "usehooks-ts";

import { Button } from "@/components/ui/button";

/** How long the copied state (check icon + tooltip) sticks around. */
const COPIED_RESET_MS = 1200;

interface CopyValueButtonProps {
  value: string;
  /** Human label of what's being copied — used for the a11y name and tooltip. */
  label: string;
}

/**
 * Copy-to-clipboard affordance for a detail row. Confirms three ways: the icon
 * swaps to a check, the tooltip flips to "Copied", and a polite live region
 * announces it for screen readers.
 */
export const CopyValueButton = ({ value, label }: CopyValueButtonProps) => {
  const { t } = useTranslation(["common"]);
  const [copied, setCopied] = useState(false);
  const [, copy] = useCopyToClipboard();

  useTimeout(() => setCopied(false), copied ? COPIED_RESET_MS : null);

  const handleCopy = useCallback(async () => {
    const ok = await copy(value);
    if (ok) setCopied(true);
  }, [copy, value]);

  const target = label.toLowerCase();
  const Icon = copied ? CheckIcon : CopyIcon;

  return (
    <>
      <Button
        variant="ghost"
        size="icon-xs"
        onClick={handleCopy}
        aria-label={t("common:chrome.copy_value", { value: target })}
        className="shrink-0"
        data-tooltip-id="hrm-tooltip"
        data-tooltip-content={
          copied
            ? t("common:chrome.copied")
            : t("common:chrome.copy_value", { value: target })
        }
      >
        <Icon
          size={14}
          className={copied ? "text-success" : "text-muted-foreground"}
        />
      </Button>
      <span aria-live="polite" className="sr-only">
        {copied
          ? t("common:chrome.value_copied_to_clipboard", { value: label })
          : ""}
      </span>
    </>
  );
};
